import BpkSmallChevronRight from '@skyscanner/backpack-web/bpk-component-icon/sm/chevron-right';
import LocationIconSM from '@skyscanner/backpack-web/bpk-component-icon/sm/location';
import BpkLink from '@skyscanner/backpack-web/bpk-component-link';
import BpkStarRating from '@skyscanner/backpack-web/bpk-component-star-rating';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import RatingsContent from './RatingsContent';
import SaveAndShare from './SaveAndShare';

import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { UserContext } from '@shared/types/user-context';

import STYLES from './HotelInformation.module.scss';

import { useHotelBasicInfo } from '@hotels-hotel-detail/data-access/hooks/useHotelContent';
import { buildDetailsMapAttributes } from '@hotels-hotel-detail/utils/meaningful-behaviour-data/details-map';
import { HASH_FULL_MAP, HASH_REVIEWS } from '@shared/constants/hash-routes';
import BpkBreakpoint, { BREAKPOINTS } from '@shared/ui/common/BpkBreakpoint';
import RouteLink from '@shared/ui/common/RouteLink';
import { MeaningfulBehaviourDataWrapper } from '@shared/ui/contexts/meaningful-behaviour-data';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';
import { ACTION_TYPE } from '@shared/utils-frontend/mini-events/constants';
import { scrollToHotelRating } from '@shared/utils-frontend/navigation-service';

const cls = cssModules(STYLES);

type Props = {
  userContext: UserContext;
  miniEventTracker: MiniEventTracker;
};

const HotelInformation = ({
  miniEventTracker,
  userContext: { enableSEOVariantHotelsDetailsPage },
}: Props) => {
  const {
    confidentReview,
    hasNoMedia,
    hotelId,
    location: { address },
    name,
    reviewSummary,
    stars,
  } = useHotelBasicInfo();

  const onAddressClick = () => {
    miniEventTracker.trackHotelsAction(ACTION_TYPE.HOTEL_DETAIL_ADDRESS_CLICK);
  };
  const hasAvailableReviews = reviewSummary && reviewSummary.reviewCount > 0;

  return (
    <BpkBreakpoint query={BREAKPOINTS.MOBILE}>
      {(isMobile: boolean) => (
        <div
          className={cls(
            'HotelInformation',
            enableSEOVariantHotelsDetailsPage
              ? 'HotelInformation__seoMargin'
              : 'HotelInformation__funnelMargin',
            hasNoMedia ? 'HotelInformation__hasNoMedia' : '',
          )}
        >
          <div className={cls('HotelInformation__basicAndShare')}>
            <div>
              <div className={cls('HotelInformation__nameStars')}>
                <BpkText
                  textStyle={
                    isMobile ? TEXT_STYLES.heading3 : TEXT_STYLES.hero5
                  }
                  tagName="h1"
                  className={cls('HotelInformation__hotelName')}
                >
                  {name}
                </BpkText>
                {stars?.level > 0 && (
                  <BpkStarRating
                    rating={stars.level}
                    maxRating={stars.level}
                    ratingLabel={stars.label}
                    large={!isMobile}
                  />
                )}
              </div>
              <RouteLink
                href={HASH_FULL_MAP}
                onClick={onAddressClick}
                render={(onClick) => (
                  <MeaningfulBehaviourDataWrapper
                    {...buildDetailsMapAttributes({
                      hotelId,
                      item: 'static_information',
                    })}
                  >
                    <BpkLink
                      as="button"
                      implicit
                      onClick={onClick}
                      data-test-id="address-button"
                      data-testid="address-button"
                      className={cls('HotelInformation__locationAndLink')}
                    >
                      {!isMobile && (
                        <div className={cls('HotelInformation__locationIcon')}>
                          <LocationIconSM />
                        </div>
                      )}
                      <BpkText
                        textStyle={
                          isMobile ? TEXT_STYLES.footnote : TEXT_STYLES.base
                        }
                        className={cls('HotelInformation__address')}
                        tagName="p"
                      >
                        {address}
                      </BpkText>
                      {isMobile && (
                        <div className={cls('HotelInformation__chevronIcon')}>
                          <BpkSmallChevronRight />
                        </div>
                      )}
                    </BpkLink>
                  </MeaningfulBehaviourDataWrapper>
                )}
              />
            </div>
            {!isMobile && <SaveAndShare />}
          </div>
          {isMobile && hasAvailableReviews && (
            <RouteLink
              href={HASH_REVIEWS}
              render={(onClickLink) => (
                <RatingsContent
                  reviewSummary={reviewSummary}
                  isMobile={isMobile}
                  onClick={onClickLink}
                />
              )}
            />
          )}
          {!isMobile && hasAvailableReviews && (
            <RatingsContent
              confidentReview={confidentReview}
              reviewSummary={reviewSummary}
              isMobile={isMobile}
              onClick={scrollToHotelRating}
            />
          )}
        </div>
      )}
    </BpkBreakpoint>
  );
};

export default withMiniEventTracker(withUserContext(HotelInformation));
